import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import cn from 'classnames';
import Joyride, { CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { AxiosResponse } from 'axios';
import { useLiveQuery } from 'dexie-react-hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { db } from '../db';
import { Loader } from 'components/ui/Loader';
import { BaseButton } from 'components/ui/buttons';
import { Tooltip } from 'components/roster/TooltipTour';
import { detailTour, TourStepStatus } from 'types/tours';
import { Header } from 'components/details-pages/Header';
import { Footer } from 'components/details-pages/Footer';
import { StabilityLevel } from 'components/ui/StabilityLevel';
import { ActionModal, createNotification } from 'components/ui';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';
import { StartTourModal } from 'components/roster/StartTourModal';
import { LocalStorageService } from 'services/localStorageService';
import { PatientMedicalRecord } from 'record/patient-medical.record';
import { AppRoutes, replaceRouteParamWithValue } from 'routes/app-routes';
import { getComfortMeasures } from 'algorithms/comfort-measures-algorithm';
import { useLoadingWithErrorHandling } from 'hooks/useLoadingWithErrorHandling';
import { PatientHistoryAssessments } from 'components/history-assessment/PatientHistoryAssessments';
import {
  StabilityChart,
  ThanksModal,
  Recommendation,
  assessmentTourSteps,
  FollowRecommendationModal,
  EmptyBlock,
  RecommendationsModal,
  StopAssessmentModal,
  AssessmentPdfContent,
  SendButton,
} from 'components/assessment-details';
import {
  changePatientStatus,
  getAssessment,
  getAssessmentResultsByPatientId,
  getMissingAssessmentsByPatientId,
  getPatientInformation,
} from 'services/api';
import {
  CalendarIcon,
  ClockIcon,
  FeedingIcon,
  TemperatureIcon,
  StoolingIcon,
  CryingIcon,
  SleepingIcon,
  TremorsIcon,
  PrintIcon,
  LeftIcon,
  RightIcon,
  WarningCubeIcon,
  InfoIcon,
  ChartNotificationIcon,
  PillsIcon,
  DonateIcon,
} from 'assets/icons';
import {
  AssessmentResultsType,
  AssessmentResultType,
  AssessmentType,
  changeDirection,
  COMFORT_RECOMMENDATIONS,
  ComfortMeasures,
  Drugs,
  MissingAssessmentType,
  MissingAssessmentsType,
  Symptoms,
  TREATMENT_RECOMMENDATIONS,
} from 'types/assessment';
import { useAuth } from 'contexts/AuthContextProvider';
import { ClonidineRecommendation } from 'algorithms/clonidine-algorithm';
import { usePDF } from 'react-to-pdf';
import { useQrCodeSvg } from 'hooks/useQrCodeSvg';

type DiscontinueAssessmentOptions = {
  isShowDiscontinueModal: boolean;
  isDiscontinueAssessments: boolean;
  isShowConfirmationDiscontinueModal: boolean;
  isContinueAssessments: boolean;
};

export type DosesDataType = {
  currentMorphineDose: number;
  currentClonidineDose: number;
  currentClonidineFrequency: number;
};

export const STABILITY_INDICATORS = [
  {
    title: 'Feeding',
    icon: FeedingIcon,
    symptomId: Symptoms.Feeding,
    checkingScore: 0,
    stableClass: '[&>g>rect]:fill-green-900/15 [&>g>path]:stroke-green-900',
    unstableClass: '[&>g>rect]:fill-red-400 [&>g>path]:stroke-white-400',
  },
  {
    title: 'Stooling',
    icon: StoolingIcon,
    symptomId: Symptoms.Stools,
    checkingScore: 0,
    stableClass: '[&>g>rect]:fill-green-900/15 [&>g>path]:stroke-green-900',
    unstableClass: '[&>g>rect]:fill-red-400 [&>g>path]:stroke-white-400',
  },
  {
    title: 'Crying',
    icon: CryingIcon,
    symptomId: Symptoms.Crying,
    checkingScore: 0,
    stableClass: '[&>g>rect]:fill-green-900/15 [&>g>g>path]:fill-green-900 [&>g>g>g>path]:fill-green-900',
    unstableClass: '[&>g>rect]:fill-red-400 [&>g>g>path]:fill-white-400 [&>g>g>g>path]:fill-white-400',
  },
  {
    title: 'Sleeping',
    icon: SleepingIcon,
    symptomId: Symptoms.Sleep,
    checkingScore: 0,
    stableClass:
      '[&>g>rect]:fill-green-900/15 [&>g>path]:stroke-green-900 [&>g>g>path]:stroke-green-900 [&>g>ellipse]:fill-green-900',
    unstableClass:
      '[&>g>rect]:fill-red-400 [&>g>path]:stroke-white-400 [&>g>g>path]:stroke-white-400 [&>g>ellipse]:fill-white-400',
  },
  {
    title: 'Temperature',
    icon: TemperatureIcon,
    symptomId: Symptoms.Temperature,
    checkingScore: 0,
    stableClass: '[&>rect]:fill-green-900/15 [&>g>path]:stroke-green-900',
    unstableClass: '[&>rect]:fill-red-400 [&>g>path]:stroke-white-400',
  },
  {
    title: 'Tremors',
    icon: TremorsIcon,
    symptomId: Symptoms.Tremors,
    checkingScore: 3,
    stableClass: '[&>g>rect]:fill-green-900/15 [&>g>g>path]:stroke-green-900',
    unstableClass: '[&>g>rect]:fill-red-400 [&>g>g>path]:stroke-white-400',
  },
];
const MAX_RESULTS = 6;

export const AssessmentDetails: React.FC = () => {
  const [patientMedicalData, setPatientMedicalData] = useState<PatientMedicalRecord | null>(null);
  const [assessmentResults, setAssessmentResults] = useState<(AssessmentResultType | MissingAssessmentType)[]>([]);
  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState<number>(0);
  const [currentDosesData, setCurrentDosesData] = useState<DosesDataType | null>(null);
  const [isShowTourModal, setIsShowTourModal] = useState<boolean>(
    LocalStorageService.getLocalStorage(detailTour) === null,
  );
  const [isShowRecommendationsModal, setShowRecommendationsModal] = useState<boolean>(false);
  const [isShowThanksModal, setShowThanksModal] = useState<boolean>(false);
  const [isShowFollowRecommendationsModal, setShowFollowRecommendationsModal] = useState<boolean>(false);
  const [isStartTour, setIsStartTour] = useState<boolean>(false);
  const [assessment, setAssessment] = useState<AssessmentType | null>(null);

  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOnline } = useNetworkCheck();

  const { id: patientId } = useParams();

  const currentResult = useMemo(
    () => assessmentResults[currentAssessmentIndex],
    [assessmentResults, currentAssessmentIndex],
  );

  const { toPDF, targetRef } = usePDF({
    filename: `${patientMedicalData?.ref_id}__${currentResult?.created}.pdf`,
    page: { format: 'LETTER' },
    method: 'save',
  });

  const { qrCodeSvg, loading: qrLoading } = useQrCodeSvg(patientId || '');

  const restartTour = () => {
    setIsStartTour(false);
    setIsShowTourModal(true);
    LocalStorageService.removeLocalStorage(detailTour);
  };
  const assessmentResultFromDB = useLiveQuery(() => db.assessmentResults.where({ ref_id: patientId }).toArray());

  const mapDatabaseResultsToAssessmentResults = useCallback((): AssessmentResultType[] => {
    if (!assessmentResultFromDB) return [];

    return assessmentResultFromDB.map(result => {
      return {
        id: '0',
        version: result.version,
        hours_old: result.hours_old,
        created: result.created.toISOString(),
        overall_score: result.overall_score,
        assessment_result_symptom: result.assessment_result_symptom,
        assessment_result_drug: result.assessment_result_drug,
        comfortRecommendations: [],
      };
    });
  }, [assessmentResultFromDB]);

  let isCallAssessment: boolean = false;

  if (location.state) {
    ({ isCallAssessment } = location.state as { isCallAssessment: boolean });
  }

  const [isStartAssessment, setStartAssessment] = useState<boolean>(isCallAssessment || false);

  const [discontinueAssessmentOptions, setDiscontinueAssessmentOptions] = useState<DiscontinueAssessmentOptions>({
    isShowDiscontinueModal: false,
    isDiscontinueAssessments: false,
    isShowConfirmationDiscontinueModal: false,
    isContinueAssessments: false,
  });

  const { submitting, setSubmitting, showErrorModal, ErrorModalComponent } = useLoadingWithErrorHandling();

  const priorResult = useMemo(() => {
    const filtered = assessmentResults.filter(ar => 'assessment_result_drug' in ar);
    return filtered[filtered.length - 1] as AssessmentResultType;
  }, [assessmentResults]);

  const generateCSIComfortMeasures = useMemo(() => {
    if (!currentResult || !assessment || !('assessment_result_symptom' in currentResult)) return [];
    return getComfortMeasures(currentResult.assessment_result_symptom, assessment.comfort_recommendations);
  }, [currentResult, assessment]);

  const isRecommendationsNotConfirmed = useMemo(() => {
    if (!priorResult || !('assessment_result_drug' in priorResult)) return;

    const areDrugsRecommendationsNotAccepted = priorResult.assessment_result_drug.some(
      drug => drug.has_accepted_recommendation === null,
    );

    const isStandartComfortRecommendationNotAccepted =
      priorResult.comfortRecommendations &&
      (priorResult.comfortRecommendations.find(
        measure => measure.type_id === ComfortMeasures['Standard Comfort Measures'],
      )?.has_accepted_recommendation === null ??
        true);

    const isCsiComfortRecommendationNotAccepted =
      generateCSIComfortMeasures.length > 0 &&
      priorResult.comfortRecommendations &&
      (priorResult.comfortRecommendations.find(measure => measure.type_id === ComfortMeasures['CSI Comfort Measures'])
        ?.has_accepted_recommendation === null ??
        true);

    return (
      areDrugsRecommendationsNotAccepted ||
      isStandartComfortRecommendationNotAccepted ||
      isCsiComfortRecommendationNotAccepted
    );
  }, [generateCSIComfortMeasures.length, priorResult]);

  const fetchAssessment = useCallback(async () => {
    const defaultAssessmentVersion = 1;
    const version = user?.tenant_assessment_version ?? defaultAssessmentVersion;
    const { data }: AxiosResponse<AssessmentType> = await getAssessment(version);
    setAssessment(data);
  }, [setAssessment, user]);

  const fetchPatientAssessmentResults = useCallback(async () => {
    if (!patientId) return;

    setSubmitting(true);

    try {
      const [{ data: assessmentResultsData }, { data: missingAssessmentsData }]: [
        AxiosResponse<AssessmentResultsType>,
        AxiosResponse<MissingAssessmentsType>,
      ] = await Promise.all([getAssessmentResultsByPatientId(patientId), getMissingAssessmentsByPatientId(patientId)]);

      let array = assessmentResultsData.result;
      missingAssessmentsData.result.forEach(missingAssessment => {
        const index = array.findIndex(result => result.id === missingAssessment.assessment_result_id);
        array = [...array.slice(0, index - 1), missingAssessment, ...array.slice(index - 1)] as any;
      });
      const sortedResults = sortedByDate(array);
      const lastFewResults = sortedResults.slice(-MAX_RESULTS);
      setAssessmentResults(lastFewResults);
      setCurrentAssessmentIndex(lastFewResults.length - 1);
    } catch (error) {
      console.error(error);
      showErrorModal(error);
    } finally {
      setSubmitting(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchPatientData = useCallback(async () => {
    if (!patientId) return;

    setSubmitting(true);

    try {
      const { data }: AxiosResponse<PatientMedicalRecord> = await getPatientInformation(patientId);
      setPatientMedicalData(new PatientMedicalRecord(data));
    } catch (error) {
      console.error(error);
      showErrorModal(error);
    } finally {
      setSubmitting(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (isStartAssessment && priorResult) {
      handleAssessmentAction();
      setStartAssessment(false);
    }
  }, [isStartAssessment, priorResult]);

  useEffect(() => {
    if (assessmentResultFromDB?.length) {
      setAssessmentResults(mapDatabaseResultsToAssessmentResults());
    }
  }, [assessmentResultFromDB, mapDatabaseResultsToAssessmentResults]);

  useEffect(() => {
    if (isOnline) {
      Promise.all([fetchPatientAssessmentResults(), fetchPatientData(), fetchAssessment()]).catch(error => {
        console.error(error);
        showErrorModal(error);
      });
    }
  }, [isOnline, fetchPatientAssessmentResults, fetchPatientData, fetchAssessment, showErrorModal]);

  useEffect(() => {
    if (!priorResult || !('assessment_result_drug' in priorResult)) return;

    const clonidineDrug = priorResult.assessment_result_drug.find(drug => drug.drug_id === Drugs.Clonidine);

    if (clonidineDrug) {
      const clonidineRecommendationKey = clonidineDrug.recommendation_coded as keyof typeof ClonidineRecommendation;
      const clonidineRecommendation = ClonidineRecommendation[clonidineRecommendationKey];

      setDiscontinueAssessmentOptions(prev => ({
        ...prev,
        isDiscontinueAssessments:
          !patientMedicalData?.stop_assessment_date && clonidineRecommendation === ClonidineRecommendation.Discharge,
      }));
    }
  }, [priorResult, patientMedicalData]);

  const handleRouteToAssessment = () => {
    const route = replaceRouteParamWithValue(AppRoutes.Assessment, 'id', patientId);
    navigate(route, { state: currentDosesData });
  };

  const sortedByDate = (data: AssessmentResultType[]) => {
    return data.sort((a, b) => {
      const dateA = dayjs(a.created);
      const dateB = dayjs(b.created);
      return dateA.isAfter(dateB) ? 1 : -1;
    });
  };

  const noDecisionsOnTreatmentRecommendation = useMemo(() => {
    if (!priorResult || !('assessment_result_drug' in priorResult)) return [];

    return priorResult.assessment_result_drug.filter(drug => drug.has_accepted_recommendation === null);
  }, [priorResult]);

  const tourSteps = assessmentTourSteps.filter(step => document.querySelector(step.target));

  const startTour = () => {
    setIsShowTourModal(false);
    setIsStartTour(true);
  };

  const skippedTour = () => {
    setIsStartTour(false);
    setIsShowTourModal(false);
    LocalStorageService.setLocalStorage(detailTour, TourStepStatus.SKIPPED);
  };

  const handleTourCallback = ({ status, type }: CallBackProps) => {
    if (type === EVENTS.TARGET_NOT_FOUND) {
      setIsStartTour(false);
      setIsShowTourModal(false);
      LocalStorageService.setLocalStorage(detailTour, TourStepStatus.SKIPPED);
      return;
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsStartTour(false);
      setIsShowTourModal(false);

      if (status === STATUS.SKIPPED) {
        LocalStorageService.setLocalStorage(detailTour, TourStepStatus.SKIPPED);
      }
    }
  };

  const isSymptomScoreNotBelowThreshold = (symptomId: string, checkingScore: number): boolean => {
    if (!('assessment_result_symptom' in currentResult)) {
      return false;
    }
    const symptom = currentResult.assessment_result_symptom.find(s => s.symptom_id === symptomId);
    return !(!symptom || symptom.score <= checkingScore);
  };

  const isSymptomChecked = (symptomId: string): boolean => {
    if (!('symptom' in currentResult)) {
      return false;
    }
    return currentResult.symptom?.some(s => s === symptomId);
  };

  const determineDirection = useMemo(() => {
    if (assessmentResults.length < 2 || currentAssessmentIndex === 0) return changeDirection.noChange;

    const currentLevel = assessmentResults[currentAssessmentIndex].overall_score;
    const previousLevel = assessmentResults[currentAssessmentIndex - 1].overall_score;

    if (currentLevel > previousLevel) return changeDirection.decrease;
    if (currentLevel < previousLevel) return changeDirection.increase;

    return changeDirection.noChange;
  }, [assessmentResults, currentAssessmentIndex]);

  const selectResultById = (resultId: string) => {
    if (!resultId) return;

    const index = assessmentResults.findIndex(result => result.id === resultId);
    if (index !== -1) setCurrentAssessmentIndex(index);
  };

  const handleActionInDiscontinueModal = (isRouteToAssessment: boolean = false) => {
    setDiscontinueAssessmentOptions(prev => ({
      ...prev,
      isShowDiscontinueModal: false,
    }));

    if (isRouteToAssessment) {
      handleAssessmentAction();
    }
  };

  const handleConfirmationModal = () => {
    setDiscontinueAssessmentOptions(prev => ({
      ...prev,
      isShowDiscontinueModal: false,
      isShowConfirmationDiscontinueModal: true,
    }));
  };

  const handleAssessmentAction = () => {
    setShowRecommendationsModal(true);
  };

  const stopAssessment = async () => {
    if (!patientId) return;

    try {
      setSubmitting(true);
      await changePatientStatus(patientId, false);
      navigate(AppRoutes.Roster);
      createNotification({
        type: 'success',
        title: `Assessment for patient ID: ${patientMedicalData?.ref_id} successfully discontinued`,
      });
    } catch (error) {
      console.error(error);
      showErrorModal(error);
    } finally {
      setSubmitting(false);
      setDiscontinueAssessmentOptions(prev => ({
        ...prev,
        isShowConfirmationDiscontinueModal: false,
      }));
    }
  };

  if (submitting || qrLoading)
    return (
      <div className="flex items-center h-[100vh]">
        <Loader />
      </div>
    );

  if (!submitting && !assessmentResults.length)
    return (
      <div className="flex flex-col content-between gap-8 min-h-screen">
        <Header
          patientData={patientMedicalData}
          isDiscontinue={discontinueAssessmentOptions.isDiscontinueAssessments}
          onRestartTour={restartTour}
          onDiscontinue={() =>
            setDiscontinueAssessmentOptions(prev => ({
              ...prev,
              isContinueAssessments: true,
              isShowDiscontinueModal: true,
            }))
          }
          qrCodeSvg={qrCodeSvg}
        />
        <div className="flex flex-col px-4 gap-8">
          <EmptyBlock
            Icon={ChartNotificationIcon}
            title="No stability data available yet"
            description="Please enter clinical indicators to determine the current stability level"
          />
          <EmptyBlock
            Icon={PillsIcon}
            title="No treatment recommendations available yet"
            description="Recommendations will appear once clinical data is reviewed."
          />
          <EmptyBlock
            Icon={DonateIcon}
            title="No comfort recommendations available yet"
            description="Recommendations will appear here once clinical stability data is assessed."
          />
          <Footer />
        </div>
      </div>
    );

  const isMissingAssessment =
    !('assessment_result_drug' in currentResult) && !('comfortRecommendations' in currentResult);

  return (
    <div className="flex flex-col content-between min-h-screen">
      {patientMedicalData && !isMissingAssessment && assessment && (
        <AssessmentPdfContent
          targetRef={targetRef}
          patientMedicalData={patientMedicalData}
          isMissingAssessment={isMissingAssessment}
          currentResult={currentResult}
          currentAssessmentIndex={currentAssessmentIndex}
          assessmentResults={assessmentResults}
          determineDirection={determineDirection}
          STABILITY_INDICATORS={STABILITY_INDICATORS}
          isSymptomScoreNotBelowThreshold={isSymptomScoreNotBelowThreshold}
          isSymptomChecked={isSymptomChecked}
          TREATMENT_RECOMMENDATIONS={TREATMENT_RECOMMENDATIONS}
          fetchPatientAssessmentResults={fetchPatientAssessmentResults}
          assessment={assessment}
          COMFORT_RECOMMENDATIONS={COMFORT_RECOMMENDATIONS}
          qrCodeSvg={qrCodeSvg}
        />
      )}
      <Header
        patientData={patientMedicalData}
        isDiscontinue={discontinueAssessmentOptions.isDiscontinueAssessments}
        onRestartTour={restartTour}
        onDiscontinue={() =>
          setDiscontinueAssessmentOptions(prev => ({
            ...prev,
            isContinueAssessments: true,
            isShowDiscontinueModal: true,
          }))
        }
        onBeforeAssessmentAction={handleAssessmentAction}
        qrCodeSvg={qrCodeSvg}
      />

      <div className="flex flex-col content-between w-full max-w-[768px] mx-auto">
        <div className="flex flex-col gap-6 px-6 md:px-0">
          <div data-tour="toggle-assessment" className="toggle-assessment flex justify-between items-center py-6 ">
            <div className="flex items-end">
              <div>
                <p className="text-gray-500 dark:text-silver-400 text-sm font-normal leading-[130%]">Assessment:</p>
                {isMissingAssessment ? (
                  <p className="relative flex pt-1 items-center gap-1.5 text-gray-900 dark:text-silver-400 text-[19px] font-semibold leading-7 ">
                    <CalendarIcon />
                    <span className="dark:text-white-400">Unknown Date</span>
                  </p>
                ) : (
                  <div className="flex pt-1  gap-2 md:gap-6">
                    <p className="relative flex items-center gap-1.5 text-gray-900 dark:text-silver-400 text-[19px] font-semibold leading-7 ">
                      <span className="hidden md:block absolute top-1 -right-[12px] w-[1px] h-[20px] bg-mirage/30" />
                      <CalendarIcon className="hidden md:block" />
                      <span className="dark:text-white-400">{dayjs(currentResult.created).format('MM/DD/YYYY')}</span>
                    </p>
                    <p className="flex items-center gap-1.5 text-gray-900 dark:text-silver-400 text-[19px] font-semibold leading-7">
                      <ClockIcon className="hidden md:block" />
                      <span className="dark:text-white-400">{dayjs(currentResult.created).format('HH:mm')}</span>
                    </p>
                  </div>
                )}
                {isOnline && currentAssessmentIndex === assessmentResults.length - 1 && (
                  <p className="flex w-fit md:hidden bg-teal-300/25 px-2.5 py-1 rounded-full text-gray-900 dark:text-white-200 text-xs font-medium leading-[135%]">
                    Last assessment
                  </p>
                )}
              </div>
              {isOnline && currentAssessmentIndex === assessmentResults.length - 1 && (
                <p className="ml-3 hidden md:flex bg-teal-300/25 px-2.5 py-1 rounded-full text-gray-900 dark:text-white-200 text-xs font-medium leading-[135%]">
                  Last assessment
                </p>
              )}
            </div>
            {isOnline && (
              <div className="flex">
                <BaseButton
                  scheme={'secondary'}
                  icon={LeftIcon}
                  className="flex p-3 rounded-[12px_0px_0px_12px] focus:border-green-400 focus:bg-green-200 disabled:opacity-30"
                  disabled={currentAssessmentIndex === 0}
                  onClick={() => setCurrentAssessmentIndex(prev => prev - 1)}
                />
                <BaseButton
                  scheme={'secondary'}
                  icon={RightIcon}
                  className="flex p-3 rounded-[0px_12px_12px_0px] focus:border-green-400 focus:bg-green-200 disabled:opacity-30"
                  disabled={currentAssessmentIndex === assessmentResults.length - 1}
                  onClick={() => setCurrentAssessmentIndex(prev => prev + 1)}
                />
              </div>
            )}
          </div>

          <div
            data-tour="monitor-stability"
            className="monitor-stability flex flex-col gap-4 bg-white-400 dark:bg-gray-900 shadow-section pt-6 px-6 pb-[22px] rounded-2xl"
          >
            {!isOnline && (
              <>
                <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">Stability Level</h2>
                <StabilityLevel
                  level={currentResult.overall_score}
                  levelDirection={changeDirection.noChange}
                  isShortVersion
                />
                <h2 className={cn('mt-3 text-gray-900 dark:text-white-400 text-base font-semibold leading-7 pb-3')}>
                  Clinical Stability Indicators (CSI)
                </h2>
              </>
            )}
            {isOnline && (
              <>
                <div className="hidden md:block">
                  <StabilityChart
                    currentLevelIndex={currentAssessmentIndex}
                    stabilityLevels={assessmentResults.map(result => result.overall_score)}
                    levelDirection={determineDirection}
                    onSelectResult={(index: number) => setCurrentAssessmentIndex(index)}
                  />
                </div>
                <div className="block md:hidden">
                  <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">
                    Stability Level
                  </h2>
                  <StabilityLevel
                    level={currentResult.overall_score}
                    levelDirection={changeDirection.noChange}
                    // isShortVersion
                  />
                </div>
                <h2
                  className={cn(
                    'mt-3 md:-mt-6 text-gray-900 dark:text-white-400 text-base font-semibold leading-7 pb-3',
                  )}
                >
                  Clinical Stability Indicators (CSI)
                </h2>
              </>
            )}
            <div className="flex flex-wrap">
              {STABILITY_INDICATORS.map((indicator, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center gap-2.5 w-1/2 md:w-1/6 my-[11.5px] border-r-[1px] last:border-none border-white-300"
                >
                  <indicator.icon
                    className={
                      isSymptomScoreNotBelowThreshold(indicator.symptomId, indicator.checkingScore) ||
                      isSymptomChecked(indicator.symptomId)
                        ? indicator.unstableClass
                        : indicator.stableClass
                    }
                  />
                  <p className="text-gray-900 dark:text-white-400 text-center text-xs font-medium leading-[17px]">
                    {indicator.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {isRecommendationsNotConfirmed && !isMissingAssessment && (
            <div className="flex items-center gap-2 self-stretch border bg-red-100 px-3.5 py-3 rounded-full border-solid border-red-200 text-red-400">
              <InfoIcon />
              <span className="text-gray-900 text-sm font-medium leading-[140%]">
                Patient recommendations have not been confirmed
              </span>
            </div>
          )}

          {!isMissingAssessment ? (
            <div
              data-tour="treatment-recommendations"
              className="treatment-recommendations flex flex-col items-start gap-4 self-stretch bg-white-400 dark:bg-gray-900 p-6 shadow-section rounded-2xl"
            >
              <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">
                Treatment Recommendations
              </h2>
              <div className="flex flex-col md:flex-row justify-between gap-6 w-full">
                {TREATMENT_RECOMMENDATIONS.map(recommendation => (
                  <Recommendation
                    key={recommendation.id}
                    isComfortMeasures={false}
                    name={recommendation.name}
                    id={recommendation.id}
                    currentResult={currentResult}
                    afterUpdate={fetchPatientAssessmentResults}
                    comfortRecommendations={assessment?.comfort_recommendations ?? []}
                    isShowControls={
                      isOnline && currentResult.id === priorResult.id && !patientMedicalData?.stop_assessment_date
                    }
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyBlock
              Icon={PillsIcon}
              title="No treatment recommendations available"
              description="Recommendations are not available for the missing assessment."
            />
          )}

          {!isMissingAssessment ? (
            <div
              data-tour="comfort-recommendations"
              className="comfort-recommendations flex mb-6 flex-col items-start gap-4 self-stretch bg-white-400 dark:bg-gray-900 p-6 shadow-section rounded-2xl"
            >
              <h2 className="text-gray-900 dark:text-white-400 text-base font-semibold leading-7">
                Comfort recommendations
              </h2>
              <div className="flex flex-col md:flex-row justify-between gap-6 w-full">
                {COMFORT_RECOMMENDATIONS.map(recommendation => (
                  <Recommendation
                    key={recommendation.id}
                    isComfortMeasures
                    id={recommendation.id}
                    name={recommendation.name}
                    currentResult={currentResult}
                    afterUpdate={fetchPatientAssessmentResults}
                    comfortRecommendations={assessment?.comfort_recommendations ?? []}
                    isShowControls={
                      isOnline && currentResult.id === priorResult.id && !patientMedicalData?.stop_assessment_date
                    }
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyBlock
              Icon={DonateIcon}
              title="No comfort recommendations available"
              description="Recommendations are not available for the missing assessment."
            />
          )}
        </div>
        {isOnline && !isMissingAssessment ? (
          <div
            data-tour="send-print-btns"
            className="px-6 md:px-0 send-print-btns flex justify-center items-start gap-6 pb-10 self-stretch border-b border-gray-200 dark:border-white-400/[.10]"
          >
            <SendButton
              content={`Attached is the assessment for Patient Id ${patientMedicalData?.ref_id} performed on ${dayjs(
                currentResult?.created,
              ).format('MM/DD/YYYY HH:mm')}.`}
              filename={`${patientMedicalData?.ref_id}__${currentResult?.created}.pdf`}
              targetRef={targetRef}
              subject={`Assessment Details for ${patientMedicalData?.ref_id} from ${dayjs(currentResult.created).format(
                'MM/DD/YYYY HH:mm',
              )}`}
            />
            <BaseButton
              scheme="secondary"
              icon={PrintIcon}
              iconProps={{ className: 'w-[24px] h-[24px]' }}
              isIconOnLeftSide
              onClick={() => toPDF()}
              className="w-1/2 !p-5 !rounded-2xl !leading-[130%] dark:!bg-gray-900 !border-none"
            >
              Print
            </BaseButton>
          </div>
        ) : (
          <div className="border-b border-gray-200 pb-10 dark:border-white-400/[.10]" />
        )}

        <div className="px-6 md:px-0">
          {isOnline && (
            <PatientHistoryAssessments
              results={assessmentResults}
              currentAssessmentResultId={currentResult.id}
              selectResult={selectResultById}
            />
          )}
          <BaseButton
            scheme="secondary"
            icon={LeftIcon}
            iconProps={{ className: 'w-[24px] h-[24px]' }}
            isIconOnLeftSide
            onClick={() => navigate(AppRoutes.Roster)}
            className="w-full  !p-5 !rounded-2xl !leading-[130%] dark:!bg-gray-900 !border-none"
          >
            Back to roster
          </BaseButton>
          <Footer />
        </div>

        {isShowRecommendationsModal && (
          <RecommendationsModal
            onCloseClick={() => {
              setShowRecommendationsModal(false);
            }}
            onContinueClick={() => {
              setShowRecommendationsModal(false);
              setShowFollowRecommendationsModal(true);
            }}
          />
        )}

        {isShowFollowRecommendationsModal && patientMedicalData && (
          <FollowRecommendationModal
            patientData={patientMedicalData}
            onCloseClick={() => {
              setShowFollowRecommendationsModal(false);
              fetchPatientAssessmentResults();
            }}
            onContinueClick={(dosesData: DosesDataType) => {
              setCurrentDosesData(dosesData);
              setShowFollowRecommendationsModal(false);
              setShowThanksModal(true);
            }}
            recommendations={noDecisionsOnTreatmentRecommendation}
            lastResult={priorResult}
            fetchPatientInfo={fetchPatientData}
            comfortRecommendations={assessment?.comfort_recommendations ?? []}
          />
        )}

        {isShowThanksModal && (
          <ThanksModal onStartAssessment={handleRouteToAssessment} onCloseClick={() => setShowThanksModal(false)} />
        )}

        {discontinueAssessmentOptions.isShowDiscontinueModal && (
          <StopAssessmentModal
            isContinueAssessment={discontinueAssessmentOptions.isContinueAssessments}
            onContinue={handleActionInDiscontinueModal}
            onDiscontinue={handleConfirmationModal}
          />
        )}
        {discontinueAssessmentOptions.isShowConfirmationDiscontinueModal && (
          <ActionModal
            title={`Are you sure you want to discontinue assessments for patient ID: ${patientMedicalData?.ref_id}?`}
            description='The patient will be moved to the "Former Patients" section, and will no longer show up on your active roster. You can always re-activate them later.'
            onCancelClick={() =>
              setDiscontinueAssessmentOptions(prev => ({
                ...prev,
                isShowConfirmationDiscontinueModal: false,
              }))
            }
            onSuccessClick={stopAssessment}
            successText="Yes, discontinue"
            isSubmitting={submitting}
          />
        )}

        {discontinueAssessmentOptions.isDiscontinueAssessments && (
          <div className="fixed bottom-0 left-0 w-full px-4 py-3 flex justify-between items-center bg-gray-500/90 backdrop-blur-[5px]">
            <p className="flex items-center gap-1.5 text-white-400 text-sm font-semibold leading-[140%]">
              <WarningCubeIcon />
              You may have reached criteria to discontinue assessments.
            </p>
            <div className="flex items-center gap-3">
              <BaseButton
                className="text-silver-400 hover:text-gray-400 !border-sky"
                scheme="transparent"
                onClick={() =>
                  setDiscontinueAssessmentOptions(prev => ({
                    ...prev,
                    isShowDiscontinueModal: true,
                  }))
                }
              >
                Details
              </BaseButton>
              <BaseButton
                scheme="primary"
                onClick={() =>
                  setDiscontinueAssessmentOptions(prev => ({
                    ...prev,
                    isShowConfirmationDiscontinueModal: true,
                  }))
                }
              >
                Discontinue
              </BaseButton>
            </div>
          </div>
        )}

        <Joyride
          steps={tourSteps}
          run={isStartTour}
          callback={handleTourCallback}
          spotlightPadding={2}
          styles={{
            spotlight: {
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              borderRadius: 12,
            },
          }}
          tooltipComponent={props => (
            <Tooltip {...props} tourName={detailTour} stepsCount={tourSteps.length} skipTour={skippedTour} />
          )}
        />
        {isShowTourModal && (
          <StartTourModal
            title="Assessment complete"
            description="Since it's your first log-in, we'll show you around."
            onStartTourClick={startTour}
            onCancelClick={skippedTour}
          />
        )}

        {ErrorModalComponent}
      </div>
    </div>
  );
};
